import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { InvoiceData } from '../models/invoice-data.interface';
import { BasePdfTemplate } from './base-template';
import { getItemUnitName, getSelectedMappingLines } from './helpers/item-display.helper';

/**
 * Rathna Shree Template - A5 Size
 * Compact invoice template for smaller format
 */
export class RathnaShreeTemplate extends BasePdfTemplate {
  override templateName = 'rathna-shree';
  override templateVersion = '1.0.0';
  override description =
    'Rathna Shree invoice template - A5 size compact layout';
  override pageSize: 'A5' = 'A5';
  override previewImage = 'assets/sampleoutput/rathna-shree-sample.png';

  // Helper method to convert number to words
  private numberToWords(num: number): string {
    const ones = [
      '',
      'One',
      'Two',
      'Three',
      'Four',
      'Five',
      'Six',
      'Seven',
      'Eight',
      'Nine',
    ];
    const tens = [
      '',
      '',
      'Twenty',
      'Thirty',
      'Forty',
      'Fifty',
      'Sixty',
      'Seventy',
      'Eighty',
      'Ninety',
    ];
    const teens = [
      'Ten',
      'Eleven',
      'Twelve',
      'Thirteen',
      'Fourteen',
      'Fifteen',
      'Sixteen',
      'Seventeen',
      'Eighteen',
      'Nineteen',
    ];

    if (num === 0) return 'Zero';

    const convertLessThanThousand = (n: number): string => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100)
        return (
          tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '')
        );
      return (
        ones[Math.floor(n / 100)] +
        ' Hundred' +
        (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '')
      );
    };

    const wholePart = Math.floor(num);

    if (wholePart < 1000) return convertLessThanThousand(wholePart);
    if (wholePart < 100000) {
      const thousands = Math.floor(wholePart / 1000);
      const remainder = wholePart % 1000;
      return (
        convertLessThanThousand(thousands) +
        ' Thousand' +
        (remainder !== 0 ? ' ' + convertLessThanThousand(remainder) : '')
      );
    }

    const lakhs = Math.floor(wholePart / 100000);
    const remainder = wholePart % 100000;
    return (
      convertLessThanThousand(lakhs) +
      ' Lakh' +
      (remainder !== 0 ? ' ' + this.numberToWords(remainder) : '')
    );
  }

  override generatePdfDefinition(data: InvoiceData): TDocumentDefinitions {
    const storename = JSON.parse(localStorage.getItem('storename') || '{}');
    let storeAddress = '';
    if (storename) {
      const addressParts = [
        storename.address,
        storename.address1,
        storename.address2,
        storename.pincode,
      ].filter((part) => part && part.trim()); // Filter out empty/null values

      storeAddress = addressParts.join(', ');
    }

    const businessName = storename?.name || data.businessName || '';
    const businessAddress = storeAddress || data.businessAddress || '';
    const businessPhone = storename?.mobile || data.businessPhone || '';
    const businessGSTIN = data.businessGSTIN || '';

    const formatDate = this.formatDate.bind(this);
    const numberToWords = this.numberToWords.bind(this);

    // const calculatedTotalTax = data.items.reduce((sum, item) => {
    //   const cgstPercent = item.cgst || 0;
    //   const sgstPercent = item.sgst || 0;
    //   const igstPercent = item.igst || 0;
    //   const cessPercent = item.cess || 0;
    //   const itemTaxAmount =
    //     (item.total * (cgstPercent + sgstPercent + igstPercent + cessPercent)) /
    //     100;
    //   return sum + itemTaxAmount;
    // }, 0);
    // Fallback tax sum excludes free items
    const calculatedTotalTax = data.items.reduce((sum, item) => {
      if (Number(item.is_free) === 1) return sum;
      const cgstPercent = item.cgst || 0;
      const sgstPercent = item.sgst || 0;
      const igstPercent = item.igst || 0;
      const cessPercent = item.cess || 0;
      const itemTaxAmount =
        (item.total * (cgstPercent + sgstPercent + igstPercent + cessPercent)) /
        100;
      return sum + itemTaxAmount;
    }, 0);

    const totalTaxToDisplay = data.taxAmount || calculatedTotalTax;

    // Free items summary (shown below the TOTAL row only when free items exist)
    let totalFreeQty = 0;
    let totalFreeAmount = 0;
    (data.items || []).forEach((item: any) => {
      if (Number(item.is_free) === 1) {
        totalFreeQty += Number(item.quantity || 0);
        totalFreeAmount += Number(item.originalprice || item.mrp || 0) * Number(item.quantity || 0);
      }
    });

    // TOTAL SALES excluding free items (backend subtotal includes free item amount)
    const freeItemsAmount = (data.items || []).reduce((sum: number, item: any) =>
      Number(item.is_free) === 1
        ? sum + Number(item.amount || (item.price || 0) * (item.quantity || 0))
        : sum, 0);
    const subtotalExclFree = Math.max(0, Number(data.subtotal || 0) - freeItemsAmount);

    return {
      pageSize: this.getPageSize(),
      pageOrientation: 'portrait',
      pageMargins: [10, 10, 10, 10],

      content: [
        {
          columns: [
            {
              width: '45%',
              stack: [
                {
                  text: businessName,
                  fontSize: 10,
                  bold: true,
                  margin: [0, 0, 0, 2],
                },
                {
                  text: businessAddress,
                  fontSize: 7,
                  margin: [0, 0, 0, 2],
                },
                {
                  text: `PH:${businessPhone}`,
                  fontSize: 7,
                  margin: [0, 0, 0, 2],
                },
                ...(businessGSTIN
                  ? [
                    {
                      text: `GSTIN : ${businessGSTIN}`,
                      fontSize: 7,
                      margin: [0, 0, 0, 0],
                    },
                  ]
                  : []),
              ],
              margin: [2, 2, 2, 2],
            },
            {
              width: '45%',
              stack: [
                {
                  text: data.customerName,
                  fontSize: 8,
                  bold: true,
                  margin: [0, 0, 0, 2],
                },
                {
                  text:
                    this.getCustomerAddressLines(data).join('\n') || 'ADDRESS',
                  fontSize: 7,
                  margin: [0, 0, 0, 2],
                },
                {
                  text: data.customerAddress?.city || '',
                  fontSize: 7,
                  margin: [0, 0, 0, 2],
                },
                {
                  text: data.businessGSTIN
                    ? `GST No:${data.businessGSTIN}`
                    : '',
                  fontSize: 7,
                  margin: [0, 0, 0, 2],
                },
                {
                  text: `PH No: ${data.customerMobile || ''}`,
                  fontSize: 7,
                },
              ],
              margin: [2, 2, 2, 2],
            },
            {
              width: '10%',
              text: 'QR',
              fontSize: 8,
              alignment: 'center',
              margin: [2, 2, 2, 2],
            },
          ] as any,
          margin: [0, 0, 0, 5],
        } as any,

        // Invoice Type and Details Row
        {
          columns: [
            {
              width: '30%',
              text: 'TAX INVOICE',
              fontSize: 9,
              bold: true,
              margin: [2, 2, 2, 2],
            },
            {
              width: '50%',
              text: `Bill No.: ${data.invoiceNumber}`,
              fontSize: 8,
              alignment: 'center',
              margin: [2, 2, 2, 2],
            },
            {
              width: '20%',
              text: `Date: ${formatDate((data.invoiceDate || data.invoicedate || '') as string | Date)}`,
              fontSize: 8,
              alignment: 'right',
              margin: [2, 2, 2, 2],
            },
          ] as any,
          margin: [0, 0, 0, 3],
        } as any,

        // Items Table - Rathna Shree Style with many columns
        {
          table: {
            headerRows: 1,
            widths: [18, '*', 35, 20, 20, 28, 25, 35, 25, 30, 30, 35],
            body: [
              // Header Row
              [
                {
                  text: 'SNo',
                  fontSize: 6,
                  bold: true,
                  alignment: 'center',
                  margin: [1, 2, 1, 2],
                },
                {
                  text: 'DESCRIPTION',
                  fontSize: 6,
                  bold: true,
                  alignment: 'left',
                  margin: [1, 2, 1, 2],
                },
                {
                  text: 'HSN/HSC',
                  fontSize: 6,
                  bold: true,
                  alignment: 'center',
                  margin: [1, 2, 1, 2],
                },
                {
                  text: 'QTY',
                  fontSize: 6,
                  bold: true,
                  alignment: 'center',
                  margin: [1, 2, 1, 2],
                },
                // {
                //   text: 'Free',
                //   fontSize: 6,
                //   bold: true,
                //   alignment: 'center',
                //   margin: [1, 2, 1, 2],
                // },
                {
                  text: 'Rate',
                  fontSize: 6,
                  bold: true,
                  alignment: 'right',
                  margin: [1, 2, 1, 2],
                },
                {
                  text: 'Disc',
                  fontSize: 6,
                  bold: true,
                  alignment: 'right',
                  margin: [1, 2, 1, 2],
                },
                {
                  text: 'Taxable',
                  fontSize: 6,
                  bold: true,
                  alignment: 'right',
                  margin: [1, 2, 1, 2],
                },
                {
                  text: 'GST%',
                  fontSize: 6,
                  bold: true,
                  alignment: 'center',
                  margin: [1, 2, 1, 2],
                },
                {
                  text: 'Taxamt',
                  fontSize: 6,
                  bold: true,
                  alignment: 'right',
                  margin: [1, 2, 1, 2],
                },
                {
                  text: 'N.Rate',
                  fontSize: 6,
                  bold: true,
                  alignment: 'right',
                  margin: [1, 2, 1, 2],
                },
                {
                  text: 'Amount',
                  fontSize: 6,
                  bold: true,
                  alignment: 'right',
                  margin: [1, 2, 1, 2],
                },
              ],
              // Items
              ...data.items.map((item, index) => {
                let gstPercent: number;
                if (item.tax && item.tax > 0) {
                  gstPercent = item.tax;
                } else {
                  const totalTaxAmount =
                    (item.cgst || 0) + (item.sgst || 0) + (item.igst || 0);
                  const taxableAmount = item.discaftertax
                    ? (item.price * item.quantity)
                    : (item.total || 0);
                  // const taxableAmount = item.total; // item.total is the taxable amount (before tax)
                  if (taxableAmount > 0 && totalTaxAmount > 0) {
                    gstPercent = Math.round(
                      (totalTaxAmount / taxableAmount) * 100,
                    );
                  } else {
                    gstPercent = 0; // Default to 0% if calculation not possible
                  }
                }

                // item.total is already the taxable amount AFTER item discount
                const taxableAmount = item.total;

                const cgstPercent = item.cgst || 0;
                const sgstPercent = item.sgst || 0;
                const igstPercent = item.igst || 0;
                const cessPercent = item.cess || 0;
                const gstAmount =
                  (taxableAmount *
                    (cgstPercent + sgstPercent + igstPercent + cessPercent)) /
                  100;

                // Amount = Taxable + Tax (item discount already subtracted in taxableAmount)
                const itemTotalAmount = taxableAmount + gstAmount;

                // N.Rate is the price per single unit (not total)
                const nRate = item.price;

                // Free items: discount, taxable, GST %, tax amount and amount print zero
                const isFreeItem = Number(item.is_free) === 1;

                return [
                  {
                    text: (index + 1).toString(),
                    fontSize: 7,
                    alignment: 'center',
                    margin: [1, 2, 1, 2],
                  },
                  // { text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`, fontSize: 7, margin: [1, 2, 1, 2] },
                  // Name + selected mapping lines (one per line) + unit sub-line (QTY column too narrow for unit)
                  getSelectedMappingLines(item).length > 0 || getItemUnitName(item)
                    ? {
                      stack: [
                        { text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`, fontSize: 7 },
                        ...getSelectedMappingLines(item).map((line: string) => ({ text: line, fontSize: 6 })),
                        ...(getItemUnitName(item) ? [{ text: `Unit : ${getItemUnitName(item)}`, fontSize: 6 }] : []),
                      ],
                      margin: [1, 2, 1, 2],
                    }
                    : { text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`, fontSize: 7, margin: [1, 2, 1, 2] },
                  {
                    text: item.hsn || '',
                    fontSize: 7,
                    alignment: 'center',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: item.quantity.toString(),
                    fontSize: 7,
                    alignment: 'center',
                    margin: [1, 2, 1, 2],
                  },
                  // {
                  //   text: '0',
                  //   fontSize: 7,
                  //   alignment: 'center',
                  //   margin: [1, 2, 1, 2],
                  // },
                  {
                    text: this.formatCurrency(item.price),
                    fontSize: 7,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: !isFreeItem && item.discount
                      ? this.formatCurrency(item.discount)
                      : '',
                    fontSize: 7,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: this.formatCurrency(isFreeItem ? 0 : taxableAmount),
                    fontSize: 7,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: isFreeItem ? '0%' : `${gstPercent}%`,
                    fontSize: 7,
                    alignment: 'center',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: this.formatCurrency(isFreeItem ? 0 : gstAmount),
                    fontSize: 7,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: this.formatCurrency(nRate),
                    fontSize: 7,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: this.formatCurrency(isFreeItem ? 0 : itemTotalAmount),
                    fontSize: 7,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                ];
              }),
            ],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#000000',
            vLineColor: () => '#000000',
            paddingLeft: () => 1,
            paddingRight: () => 1,
            paddingTop: () => 1,
            paddingBottom: () => 1,
          },
          margin: [0, 0, 0, 3],
        },

        // Total Row (Grand Total BEFORE overall discount)
        {
          table: {
            widths: ['*', 40, 50],
            body: [
              [
                {
                  text: 'TOTAL',
                  fontSize: 7,
                  bold: true,
                  alignment: 'center',
                  margin: [2, 2, 2, 2],
                },
                {
                  // text: data.items
                  //   .reduce((sum, item) => sum + item.quantity, 0)
                  //   .toString(),
                  // text: data.items
                  //   .reduce(
                  //     (sum, item) =>
                  //       Number(item.is_free) === 1 ? sum : sum + item.quantity,
                  //     0,
                  //   )
                  //   .toString(),
                  // T.Qty includes free qty; only amounts exclude free items
                  text: data.items
                    .reduce((sum, item) => sum + item.quantity, 0)
                    .toString(),
                  fontSize: 7,
                  alignment: 'center',
                  margin: [2, 2, 2, 2],
                },
                {
                  text: this.formatCurrency(
                    data.totalAmount || data.grandTotal || 0,
                  ),
                  fontSize: 7,
                  alignment: 'right',
                  bold: true,
                  margin: [2, 2, 2, 2],
                },
              ],
              // Free Items row (only when free items exist)
              ...(totalFreeQty > 0
                ? [
                  [
                    {
                      text: 'Free Items :',
                      fontSize: 7,
                      bold: true,
                      alignment: 'center',
                      margin: [2, 2, 2, 2],
                    },
                    {
                      text: totalFreeQty.toString(),
                      fontSize: 7,
                      alignment: 'center',
                      margin: [2, 2, 2, 2],
                    },
                    {
                      text: this.formatCurrency(totalFreeAmount),
                      fontSize: 7,
                      alignment: 'right',
                      bold: true,
                      margin: [2, 2, 2, 2],
                    },
                  ] as any[],
                ]
                : []),
            ],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#000000',
            vLineColor: () => '#000000',
          },
          margin: [0, 0, 0, 3],
        },

        // Overall Discount Row (only if overall discount exists)
        ...(data.overallDiscount && data.overallDiscount > 0
          ? [
            {
              table: {
                widths: ['*', 40, 50],
                body: [
                  [
                    {
                      text: 'Overall Discount',
                      fontSize: 7,
                      bold: true,
                      alignment: 'right',
                      margin: [2, 2, 2, 2],
                    },
                    {
                      text: '',
                      fontSize: 7,
                      alignment: 'center',
                      margin: [2, 2, 2, 2],
                    },
                    {
                      text: `- ${this.formatCurrency(data.overallDiscount)}`,
                      fontSize: 7,
                      alignment: 'right',
                      bold: true,
                      margin: [2, 2, 2, 2],
                    },
                  ],
                ],
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#000000',
                vLineColor: () => '#000000',
              },
              margin: [0, 0, 0, 3] as [number, number, number, number],
            },
          ]
          : []),

        // Loyalty Discount Row (only if loyalty discount exists)
        ...((Number(data.loyaltyAmount) || 0) > 0
          ? [
            {
              table: {
                widths: ['*', 40, 50],
                body: [
                  [
                    {
                      text: `Loyalty Discount (${Number(data.loyaltyPoints) || 0} pts)`,
                      fontSize: 7,
                      bold: true,
                      alignment: 'right',
                      margin: [2, 2, 2, 2],
                    },
                    {
                      text: '',
                      fontSize: 7,
                      alignment: 'center',
                      margin: [2, 2, 2, 2],
                    },
                    {
                      text: `- ${this.formatCurrency(Number(data.loyaltyAmount) || 0)}`,
                      fontSize: 7,
                      alignment: 'right',
                      bold: true,
                      margin: [2, 2, 2, 2],
                    },
                  ],
                ],
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#000000',
                vLineColor: () => '#000000',
              },
              margin: [0, 0, 0, 3] as [number, number, number, number],
            },
          ]
          : []),

        (() => {
          // Group items by GST percentage
          const gstGroups = new Map<
            number,
            { salesValue: number; sgst: number; cgst: number; igst: number }
          >();

          data.items.forEach((item) => {
            // Free items are excluded from the GST-group tax breakup
            if (Number(item.is_free) === 1) return;
            let gstPercent: number;
            if (item.tax && item.tax > 0) {
              gstPercent = item.tax;
            } else {
              const totalTaxAmount =
                (item.cgst || 0) + (item.sgst || 0) + (item.igst || 0);
              const taxableAmount = item.total; // item.total is the taxable amount (before tax)
              if (taxableAmount > 0 && totalTaxAmount > 0) {
                gstPercent = Math.round((totalTaxAmount / taxableAmount) * 100);
              } else {
                gstPercent = 18;
              }
            }

            if (!gstGroups.has(gstPercent)) {
              gstGroups.set(gstPercent, {
                salesValue: 0,
                sgst: 0,
                cgst: 0,
                igst: 0,
              });
            }

            const group = gstGroups.get(gstPercent)!;
            // group.salesValue += item.total;
            const taxableBase = item.discaftertax
              ? (item.price * item.quantity)
              : (item.total || 0);
            group.salesValue += taxableBase;

            const sgstPercent = item.sgst || 0;
            const cgstPercent = item.cgst || 0;
            const igstPercent = item.igst || 0;
            group.sgst += (taxableBase * sgstPercent) / 100;
            group.cgst += (taxableBase * cgstPercent) / 100;
            group.igst += (taxableBase * igstPercent) / 100;
            // group.sgst += (item.total * sgstPercent) / 100;
            // group.cgst += (item.total * cgstPercent) / 100;
            // group.igst += (item.total * igstPercent) / 100;
          });

          const sortedGstGroups = Array.from(gstGroups.entries()).sort(
            (a, b) => a[0] - b[0],
          );

          let calculatedTotalTax = 0;
          gstGroups.forEach((group) => {
            calculatedTotalTax += group.sgst + group.cgst + group.igst;
          });

          const maxColumns = 5;
          const gstGroupsToShow = sortedGstGroups.slice(0, maxColumns);

          const widths = [40];
          for (let i = 0; i < maxColumns; i++) {
            widths.push(30, 35);
          }

          const headerRow: any[] = [
            { text: 'Details', fontSize: 6, bold: true, margin: [1, 2, 1, 2] },
          ];
          for (let i = 0; i < maxColumns; i++) {
            headerRow.push(
              {
                text: 'Rate',
                fontSize: 6,
                bold: true,
                alignment: 'center',
                margin: [1, 2, 1, 2],
              },
              {
                text: 'Amount',
                fontSize: 6,
                bold: true,
                alignment: 'right',
                margin: [1, 2, 1, 2],
              },
            );
          }

          const salesValueRow: any[] = [
            { text: 'Sales value', fontSize: 6, margin: [1, 2, 1, 2] },
          ];
          for (let i = 0; i < maxColumns; i++) {
            if (i < gstGroupsToShow.length) {
              const [gstPercent, group] = gstGroupsToShow[i];
              salesValueRow.push(
                {
                  text: `${gstPercent}%`,
                  fontSize: 6,
                  alignment: 'center',
                  margin: [1, 2, 1, 2],
                },
                {
                  text: this.formatCurrency(group.salesValue),
                  fontSize: 6,
                  alignment: 'right',
                  margin: [1, 2, 1, 2],
                },
              );
            } else {
              salesValueRow.push(
                {
                  text: '',
                  fontSize: 6,
                  alignment: 'center',
                  margin: [1, 2, 1, 2],
                },
                {
                  text: '',
                  fontSize: 6,
                  alignment: 'right',
                  margin: [1, 2, 1, 2],
                },
              );
            }
          }

          const isInterState = this.isInterStateTax(data);

          const taxRows: any[] = [];

          if (isInterState) {
            const igstRow: any[] = [
              { text: 'IGST - Tax', fontSize: 6, margin: [1, 2, 1, 2] },
            ];
            for (let i = 0; i < maxColumns; i++) {
              if (i < gstGroupsToShow.length) {
                const [gstPercent, group] = gstGroupsToShow[i];
                igstRow.push(
                  {
                    text: `${gstPercent.toFixed(2)}%`,
                    fontSize: 6,
                    alignment: 'center',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: group.igst > 0 ? this.formatCurrency(group.igst) : '',
                    fontSize: 6,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                );
              } else {
                igstRow.push(
                  {
                    text: '',
                    fontSize: 6,
                    alignment: 'center',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: '',
                    fontSize: 6,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                );
              }
            }
            taxRows.push(igstRow);
          } else {
            const sgstRow: any[] = [
              { text: 'SGST - Tax', fontSize: 6, margin: [1, 2, 1, 2] },
            ];
            for (let i = 0; i < maxColumns; i++) {
              if (i < gstGroupsToShow.length) {
                const [gstPercent, group] = gstGroupsToShow[i];
                const sgstPercent = gstPercent / 2;
                sgstRow.push(
                  {
                    text: `${sgstPercent.toFixed(2)}%`,
                    fontSize: 6,
                    alignment: 'center',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: group.sgst > 0 ? this.formatCurrency(group.sgst) : '',
                    fontSize: 6,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                );
              } else {
                sgstRow.push(
                  {
                    text: '',
                    fontSize: 6,
                    alignment: 'center',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: '',
                    fontSize: 6,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                );
              }
            }

            const cgstRow: any[] = [
              { text: 'CGST - Tax', fontSize: 6, margin: [1, 2, 1, 2] },
            ];
            for (let i = 0; i < maxColumns; i++) {
              if (i < gstGroupsToShow.length) {
                const [gstPercent, group] = gstGroupsToShow[i];
                const cgstPercent = gstPercent / 2;
                cgstRow.push(
                  {
                    text: `${cgstPercent.toFixed(2)}%`,
                    fontSize: 6,
                    alignment: 'center',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: group.cgst > 0 ? this.formatCurrency(group.cgst) : '',
                    fontSize: 6,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                );
              } else {
                cgstRow.push(
                  {
                    text: '',
                    fontSize: 6,
                    alignment: 'center',
                    margin: [1, 2, 1, 2],
                  },
                  {
                    text: '',
                    fontSize: 6,
                    alignment: 'right',
                    margin: [1, 2, 1, 2],
                  },
                );
              }
            }
            taxRows.push(sgstRow, cgstRow);
          }

          return {
            table: {
              widths: widths,
              body: [headerRow, salesValueRow, ...taxRows],
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => '#000000',
              vLineColor: () => '#000000',
              paddingLeft: () => 1,
              paddingRight: () => 1,
              paddingTop: () => 1,
              paddingBottom: () => 1,
            },
            margin: [0, 0, 0, 3],
          };
        })(),

        {
          columns: [
            {
              width: '60%',
              stack: [
                {
                  // text: `TOTAL SALES : ${this.formatCurrency(data.subtotal)}    TAX : ${this.formatCurrency(totalTaxToDisplay)}`,
                  text: `TOTAL SALES : ${this.formatCurrency(subtotalExclFree)}    TAX : ${this.formatCurrency(totalTaxToDisplay)}`,
                  fontSize: 7,
                  margin: [2, 2, 2, 2],
                },
                {
                  text: `(Rupees ${numberToWords((data.totalAmount || data.grandTotal || 0) - (Number(data.loyaltyAmount) || 0))} only)`,
                  fontSize: 7,
                  italics: true,
                  margin: [2, 2, 2, 2],
                },
              ],
            },
            {
              width: '40%',
              stack: [
                {
                  text: `Net Amount...  ${this.formatCurrency((data.totalAmount || data.grandTotal || 0) - (Number(data.loyaltyAmount) || 0))}`,
                  fontSize: 8,
                  bold: true,
                  alignment: 'right',
                  margin: [2, 2, 2, 2],
                },
              ],
            },
          ] as any,
          margin: [0, 0, 0, 5],
        } as any,

        {
          columns: [
            {
              width: '50%',
              text: `For ${data.businessName || ''}`,
              fontSize: 7,
              margin: [2, 10, 2, 2],
            },
            {
              width: '50%',
              text: '',
              fontSize: 7,
            },
          ] as any,
          margin: [0, 5, 0, 0],
        } as any,

        {
          text: '',
          fontSize: 6,
          margin: [2, 20, 2, 2],
        },
      ].filter(
        (item): item is Exclude<typeof item, null> => item !== null,
      ) as any,

      styles: {
        businessName: {
          fontSize: 18,
          bold: true,
        },
        businessAddress: {
          fontSize: 9,
        },
        invoiceTypeBanner: {
          fontSize: 14,
          bold: true,
        },
        tableHeader: {
          fontSize: 8,
          bold: true,
        },
        tableCell: {
          fontSize: 8,
        },
      },
    };
  }

  private getTotalCGST(invoice: InvoiceData): number {
    // return (
    //   invoice.cgst ||
    //   invoice.items.reduce(
    //     (sum: any, product: any) =>
    //       sum + (product.cgstval || product.cgstAmount || 0),
    //     0,
    //   )
    // );
    // Fallback item sum excludes free items
    return (
      invoice.cgst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          Number(product.is_free) === 1
            ? sum
            : sum + (product.cgstval || product.cgstAmount || 0),
        0,
      )
    );
  }

  private getTotalSGST(invoice: InvoiceData): number {
    // return (
    //   invoice.sgst ||
    //   invoice.items.reduce(
    //     (sum: any, product: any) =>
    //       sum + (product.sgstval || product.sgstAmount || 0),
    //     0,
    //   )
    // );
    // Fallback item sum excludes free items
    return (
      invoice.sgst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          Number(product.is_free) === 1
            ? sum
            : sum + (product.sgstval || product.sgstAmount || 0),
        0,
      )
    );
  }

  private getTotalIGST(invoice: InvoiceData): number {
    // return (
    //   invoice.igst ||
    //   invoice.items.reduce(
    //     (sum: any, product: any) =>
    //       sum + (product.igstval || product.igstAmount || 0),
    //     0,
    //   )
    // );
    // Fallback item sum excludes free items
    return (
      invoice.igst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          Number(product.is_free) === 1
            ? sum
            : sum + (product.igstval || product.igstAmount || 0),
        0,
      )
    );
  }

  private isInterStateTax(invoice: InvoiceData): boolean {
    const igst = this.getTotalIGST(invoice);
    const cgst = this.getTotalCGST(invoice);
    const sgst = this.getTotalSGST(invoice);

    return igst > 0 && cgst === 0 && sgst === 0;
  }
}
